import { EPHEMERAL_ATTRS } from 'client-portal/models/card';
import { callbackError } from 'client-portal/utils/error-handling';
import { createElement, error } from 'client-portal/utils/banners';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import Mixin from '@ember/object/mixin';

// eslint-disable-next-line ember/no-new-mixins
export default Mixin.create({
  iframe: service(),
  pendingAppointment: service(),
  currentPractice: service(),
  analytics: service(),
  clientPortalTracking: service(),
  store: service(),
  session: service(),
  prescreener: service(),
  signOut: service(),
  intl: service(),

  getAppointment() {
    return this.getInformationModel().appointment;
  },

  getInformationModel() {
    let { pendingAppointment } = this;
    if (!pendingAppointment.appointment) {
      let reservation = this.modelFor(this.request.baseRouteName);
      let clientParams = this.paramsFor('-request');
      pendingAppointment.updatePendingAppointmentData({ reservation, clientParams });
    }
    return pendingAppointment.informationModel;
  },

  errorContent(message) {
    let title, content;

    if (message.title === 'duplicate-couple-email') {
      title = this.intl.t('errors.appointment.duplicateEmail.title');
      content = this.intl.t('errors.appointment.duplicateEmail.coupleMessage');
    } else if (message.title === 'duplicate-minor-email') {
      title = this.intl.t('errors.appointment.duplicateEmail.title');
      content = this.intl.t('errors.appointment.duplicateEmail.minorMessage');
    } else {
      title = this.intl.t('errors.appointment.unableToRequest.title');
      content = message.title;
    }

    content = createElement('p', { innerHTML: content });

    return { title, content };
  },

  validateAndPersistAppointmentTask: task(function* (stripeElements) {
    try {
      yield this.validateFieldsTask.perform(stripeElements);
      yield this.persistAppointmentTask.perform();
    } catch (err) {
      if (err) console.error(err); // eslint-disable-line no-console
      // show validation messages
      // persistAppointmentTask handles its own errors
    }
  }),

  validatePrescreenerTask: task(function* () {
    if (!this.prescreener.validation.validateAll()) {
      return Promise.reject();
    }
  }),

  validateFieldsTask: task(function* (stripeElements) {
    let { card, client } = this.getAppointment();

    client.set('_isDirty', true);
    yield client.validate();

    if (this.prescreener.featurePrescreenerV2) {
      yield this.validatePrescreenerTask.perform();
    }

    if (client.validations.isInvalid) {
      return Promise.reject();
    }

    if (card) {
      card.set('_isDirty', true);
      const isCardValid = yield this._handleCreditCardCreation(card, stripeElements);
      if (!isCardValid) {
        return Promise.reject();
      }
    }
  }),

  createCardTokenTask: task(function* (card) {
    try {
      yield card.createToken();
      return true;
    } catch {
      return false;
    }
  }),

  async _sendWidgetInsuranceCardFile(clientId, file) {
    let { store } = this;
    return !(await store
      .createRecord('unauthenticated-insurance-card', {
        file,
        clientId,
      })
      .save());
  },

  persistAppointmentTask: task(function* () {
    let appointment = this.getAppointment();

    try {
      let { card, client } = appointment;
      let { insuranceCardFrontSideFile, insuranceCardBackSideFile } = client;

      let opts = {};
      let { isNew: isNewClient } = client;

      if (isNewClient) {
        opts = {
          adapterOptions: {
            query: { include: 'client,card,office' },
            embed: [
              'client',
              'card',
              'clientRelationships',
              'relatedClient',
              'emails',
              'phones',
              ...(this.prescreener.featurePrescreenerV2 ? ['prescreenerAnswer'] : []),
            ],
          },
        };
      }

      if (
        !this.prescreener.featurePrescreenerV2 &&
        (insuranceCardFrontSideFile || insuranceCardBackSideFile)
      ) {
        appointment.set(
          'files',
          [insuranceCardFrontSideFile, insuranceCardBackSideFile]
            .filter(Boolean)
            .map(uploadFile => uploadFile.file)
        );
      }
      this.prescreener.refreshAnswerRecord();

      yield appointment.save(opts);

      let relationship = client.clientRelationships.slice().pop();
      appointment.client.isMinor = client.isMinor;
      appointment.relatedClient = relationship?.relatedClient;
      if (isNewClient) {
        this.session.newProspectiveClient = appointment.client;
      }

      this.session.set('data.skipLicenseAgreement', isNewClient);
      this.session.set('data.skipLegalAgreements', isNewClient);

      // Re-attach data to a new card in order for validations to not
      // display invalid while we're transitioning
      if (card) {
        EPHEMERAL_ATTRS.forEach(x => appointment.card.set(x, card[x]));
      }

      let reservation = this.modelFor(this.request.baseRouteName);
      reservation.set('appointment', appointment);

      let {
        cptCode: { id, description, duration, rate },
        office: { isVideo },
        clinician: { id: clinicianId },
        message,
        reasonsForVisit,
        mentalHealthHistory,
        careTypes,
      } = reservation;
      let clientMessageSent = !!message;
      let { id: appointmentId, insurancePaymentMethod } = appointment;
      let hasReasonsForVisit = !!reasonsForVisit?.length;
      let hasMentalHealthHistory = !!mentalHealthHistory?.length;
      let hasPaymentMethod = ['Insurance', 'Self-pay'].includes(insurancePaymentMethod);
      let isPaymentInsurance = insurancePaymentMethod === 'Insurance';
      let {
        featureStickyCtaBookingWidget,
        featureExpandPrescreenerQuestions,
        featureWidgetPaymentMethod,
        featureWidgetPaymentMethodDesktop,
        featureWidgetPaymentMethodGaRollout,
      } = this.currentPractice.getProperties(
        'featureStickyCtaBookingWidget',
        'featureExpandPrescreenerQuestions',
        'featureWidgetPaymentMethod',
        'featureWidgetPaymentMethodDesktop',
        'featureWidgetPaymentMethodGaRollout'
      );
      let hasCareTypes = !!careTypes?.length;
      let appointmentProperties = {
        'service_code': id,
        'service_duration': duration,
        'service_name': description,
        'service_price': rate,
        'source': this.request.sourceType,
        'client_message_sent': clientMessageSent,
        'is_telehealth': isVideo,
        'client_type': this.request.findClientType(this.session.currentClient),
        'clinician_id': clinicianId,
        'reason_for_visit_message': clientMessageSent,
        'feature_booking_widget_reordering_steps': this.session.reorderedNav,
        'feature_static_reason_for_visit': this.session.addReasonsForVisitFlag,
        'feature_sticky_cta_booking_widget': featureStickyCtaBookingWidget,
        'reason_for_visit_checkbox': hasReasonsForVisit,
        'feature_expand_prescreener_questions': featureExpandPrescreenerQuestions,
        'feature_widget_payment_method': featureWidgetPaymentMethod,
        'feature_widget_payment_method_desktop': featureWidgetPaymentMethodDesktop,
        'feature_widget_payment_method_GA_rollout': featureWidgetPaymentMethodGaRollout,
        'risk_assessment': hasMentalHealthHistory,
        'medication_question': hasCareTypes,
        'payment_question': hasPaymentMethod,
        'referral_channel': this.session.referralChannel,
      };
      if (insurancePaymentMethod === 'Insurance') {
        appointmentProperties = {
          ...appointmentProperties,
          ...(isPaymentInsurance && { 'insurance_front_upload': !!insuranceCardFrontSideFile }),
          ...(isPaymentInsurance && { 'insurance_back_upload': !!insuranceCardBackSideFile }),
        };
      }

      let analyticsEventName = 'Appointment Request Completed';
      let analyticsProperties = {
        object: 'Appointment',
        action: 'Completed',
        'object_id': appointmentId,
        ...appointmentProperties,
      };

      isNewClient
        ? this.analytics.trackAnonymously(analyticsEventName, analyticsProperties)
        : this.analytics.track(analyticsEventName, analyticsProperties);

      this.mixpanel.track(
        `user: ${this.request.trackingPrefix} Requested Appointment`,
        {
          'feature_booking_widget_reordering_steps': this.session.reorderedNav,
          'feature_static_reason_for_visit': this.session.addReasonsForVisitFlag,
          'feature_sticky_cta_booking_widget': featureStickyCtaBookingWidget,
          'feature_expand_prescreener_questions': featureExpandPrescreenerQuestions,
          'feature_widget_payment_method': featureWidgetPaymentMethod,
          'feature_widget_payment_method_desktop': featureWidgetPaymentMethodDesktop,
          'feature_widget_payment_method_GA_rollout': featureWidgetPaymentMethodGaRollout,
          'risk_assessment': hasMentalHealthHistory,
          'medication_question': hasCareTypes,
          'payment_question': hasPaymentMethod,
          ...(isPaymentInsurance && { 'insurance_front_upload': !!insuranceCardFrontSideFile }),
          ...(isPaymentInsurance && { 'insurance_back_upload': !!insuranceCardBackSideFile }),
        },
        {
          'send_immediately': true,
        }
      );
      this.mixpanel.trackWidgetEvents('completed', {
        ...appointmentProperties,
        appointmentId,
      });
      this.iframe.appointmentRequested({
        clientMessageSent,
        appointmentId,
        reasonForVisitMessage: clientMessageSent,
        hasReasonsForVisit,
        hasMentalHealthHistory,
        hasCareTypes,
        hasPaymentMethod,
        featureWidgetPaymentMethod,
        isPaymentInsurance,
        insuranceFrontUpload: !!insuranceCardFrontSideFile,
        insuranceBackUpload: !!insuranceCardBackSideFile,
      });
      this.request.transitionTo(this, 'confirmation');
      this.send('refreshEnvironment');
    } catch (err) {
      let errorHandled = yield this.isPrescreenerBadSignoutError(err);

      let showErrorMessage =
        !appointment.card ||
        appointment.card.validations.isValid ||
        (this.session.canUsePaymentMethods && appointment.card.paymentMethodId);

      if (!appointment.client.isNew) {
        this.request.transitionTo(this, 'date');
      }

      if (showErrorMessage && !errorHandled) {
        callbackError(err, message => {
          let errorContent = this.errorContent(message);
          this.clientPortalTracking.trackErrorOnAppointmentsPage(errorContent.title);
          error(errorContent);
          return true;
        });
      }
    }
  }).drop(),

  setupController(controller) {
    this._super(...arguments);
    controller.setProperties({
      validateFieldsTask: this.validateFieldsTask,
      persistAppointmentTask: this.persistAppointmentTask,
      validatePrescreenerTask: this.validatePrescreenerTask,
      validateAndPersistAppointmentTask: this.validateAndPersistAppointmentTask,
    });
  },

  async isPrescreenerBadSignoutError(err) {
    if (this.prescreener.featurePrescreenerV2 && !this.triedSiginingOut) {
      // sometimes the prescreener will get in the way of creating appointments
      // because a user looks previously signed in. If this occurs the client
      // will be signed out and another attempt will be made to set the appointment
      // this happens because a previous appointment creation was not properly
      // signed out and the session is still valid for another user

      let errors = err.errors ?? [];
      let signOutProgress = this.trySigningOut(errors);

      return await this.hasTriedSigningOut(signOutProgress);
    }

    return false;
  },

  trySigningOut(errors) {
    const UNAUTHORIZED = '403';
    const PRESCREENER_BAD_SIGNOUT = 'You have no access to this client/clients';

    return errors
      .map(error => {
        if (
          error.code === UNAUTHORIZED &&
          error.title === PRESCREENER_BAD_SIGNOUT &&
          !this.triedSiginingOut
        ) {
          this.triedSiginingOut = true;
          return this.signOut.signOutSilently(this.persistAppointmentTask.perform);
        }
      })
      .filter(Boolean);
  },

  async hasTriedSigningOut(signOutProgress) {
    if (this.triedSiginingOut) {
      await Promise.allSettled(signOutProgress);
      return true;
    }
  },

  async _handleCreditCardCreation(card, stripeElements) {
    if (this.session.canUsePaymentMethods) {
      return await this._handleStripePaymentMethodFlow(card, stripeElements);
    } else {
      return await this._handleStripeCardTokenFlow(card);
    }
  },

  async _handleStripePaymentMethodFlow(card, stripeElements) {
    if (card.paymentMethodId) {
      return true;
    }

    const { error } = await stripeElements.submit();
    if (error || !card.name) {
      return false;
    }

    return !!(await this.stripeCards.addPaymentMethodTask.perform({
      elements: stripeElements,
      newCard: card,
      persistCard: false,
    }));
  },

  async _handleStripeCardTokenFlow(card) {
    if (card.customStripeCardId) {
      return true;
    }

    await card.validate();
    const isCardValid = card.validations.isValid;

    return isCardValid && (await this.createCardTokenTask.perform(card));
  },
});
